import {
  AnnouncementTarget,
  AnnouncementType,
} from '@/store/api/endpoints/client/announcementsApi';
import type {
  AnnouncementResponse,
  GetActiveAnnouncementsParams,
} from '@/store/api/endpoints/client/announcementsApi';
import {
  ANNOUNCEMENT_TYPE_PRIORITY,
  VISIBLE_ANNOUNCEMENT_TYPES,
  announcementMatchesPageContext,
  type AnnouncementPageContext,
} from '@/components/features/announcements/announcement-meta';
import { TenantModule } from '@/store/slices/tenantSlice';

export const ANNOUNCEMENT_TYPE_SEARCH_PARAM = 'type';
export const ANNOUNCEMENT_ID_SEARCH_PARAM = 'id';

export function parseAnnouncementTypeFilter(
  value: string | null,
): AnnouncementType | 'all' {
  if (!value) return 'all';
  return (VISIBLE_ANNOUNCEMENT_TYPES as readonly string[]).includes(value)
    ? (value as AnnouncementType)
    : 'all';
}

/** Deep link from the issue/maintenance banner or shareable filtered list URLs. */
export function buildAnnouncementsPagePath(options: {
  type?: AnnouncementType;
  id?: string;
}): string {
  const params = new URLSearchParams();
  if (options.type) params.set(ANNOUNCEMENT_TYPE_SEARCH_PARAM, options.type);
  if (options.id) params.set(ANNOUNCEMENT_ID_SEARCH_PARAM, options.id);
  const qs = params.toString();
  return qs ? `/announcements?${qs}` : '/announcements';
}

/**
 * Reduces an announcement's rich-text `message` (markdown / TipTap HTML — see
 * {@link AnnouncementMessage}) to a short plain-text preview for compact
 * surfaces like the dashboard update card. Strips tags and the most common
 * markdown syntax, collapses whitespace, then truncates with an ellipsis.
 */
export function getAnnouncementMessagePreview(
  message: string,
  maxLength = 60,
): string {
  const plain = message
    .replace(/<[^>]*>/g, ' ') // HTML tags (TipTap output)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // markdown images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // markdown links → link text
    .replace(/[*_`~#>]/g, '') // emphasis / heading / quote markers
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength).trimEnd()}…`;
}

/** Shared with dashboard card and announcements page so RTK Query reuses one cache entry. */
export const ACTIVE_ANNOUNCEMENTS_QUERY_PARAMS = {
  PageSize: 100,
  OrderBy: 'startDate:desc',
} satisfies GetActiveAnnouncementsParams;

export const ISSUE_MAINTENANCE_BANNER_HEIGHT_PX = 40;
export const DASHBOARD_NEWS_BANNER_HEIGHT_PX = 40;

/**
 * Persisted in localStorage (survives refresh/logout).
 * Value is a JSON array of dismissed announcement ids — multiple can be active at once,
 * so we track each dismiss and skip them when picking the next banner candidate.
 */
export const ISSUE_MAINTENANCE_BANNER_DISMISSED_KEY = 'issue-maintenance-banner-dismissed';

/** Same persistence shape as {@link ISSUE_MAINTENANCE_BANNER_DISMISSED_KEY}, separate key/banner family. */
export const DASHBOARD_NEWS_BANNER_DISMISSED_KEY = 'dashboard-news-banner-dismissed';

/** Shared dismissed-id storage logic for the two independent banner families (Issue/Maintenance, News/NewVersion). */
function createDismissedBannerTracker(storageKey: string) {
  function read(): Set<string> {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return new Set();

      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return new Set(parsed.filter((id): id is string => typeof id === 'string'));
        }
        if (typeof parsed === 'string') {
          return new Set([parsed]);
        }
      } catch {
        // Legacy: single id stored before we switched to a JSON array
        return new Set([raw]);
      }

      return new Set();
    } catch {
      return new Set();
    }
  }

  function dismiss(announcementId: string): void {
    try {
      const dismissed = read();
      dismissed.add(announcementId);
      localStorage.setItem(storageKey, JSON.stringify([...dismissed]));
    } catch {
      // Storage unavailable (private browsing, etc.)
    }
  }

  /**
   * Drop dismissals for announcements that are no longer active.
   * Only call after a successful fetch — while loading, active ids are unknown.
   */
  function prune(activeBannerIds: Iterable<string>): void {
    const active = new Set(activeBannerIds);
    const dismissed = read();
    const pruned = [...dismissed].filter((id) => active.has(id));
    if (pruned.length === dismissed.size) return;
    localStorage.setItem(storageKey, JSON.stringify(pruned));
  }

  return { read, dismiss, prune };
}

const issueMaintenanceDismissedTracker = createDismissedBannerTracker(
  ISSUE_MAINTENANCE_BANNER_DISMISSED_KEY,
);
export const readDismissedIssueMaintenanceBannerIds = issueMaintenanceDismissedTracker.read;
export const dismissIssueMaintenanceBanner = issueMaintenanceDismissedTracker.dismiss;
export const pruneDismissedIssueMaintenanceBannerIds = issueMaintenanceDismissedTracker.prune;

const dashboardNewsDismissedTracker = createDismissedBannerTracker(
  DASHBOARD_NEWS_BANNER_DISMISSED_KEY,
);
export const readDismissedDashboardNewsBannerIds = dashboardNewsDismissedTracker.read;
export const dismissDashboardNewsBanner = dashboardNewsDismissedTracker.dismiss;
export const pruneDismissedDashboardNewsBannerIds = dashboardNewsDismissedTracker.prune;

const BANNER_TYPES = new Set<AnnouncementType>([
  AnnouncementType.Issue,
  AnnouncementType.Maintenance,
]);

export function getIssueMaintenanceAnnouncementIds(
  items: AnnouncementResponse[] | undefined,
): string[] {
  return (items ?? []).filter((item) => BANNER_TYPES.has(item.type)).map((item) => item.id);
}

export function pickIssueMaintenanceBannerAnnouncement(
  items: AnnouncementResponse[] | undefined,
  dismissedIds?: ReadonlySet<string>,
  pageContext?: AnnouncementPageContext,
): AnnouncementResponse | null {
  const candidates = (items ?? []).filter(
    (item) =>
      BANNER_TYPES.has(item.type) &&
      !dismissedIds?.has(item.id) &&
      announcementMatchesPageContext(item.appliesTo, pageContext),
  );
  if (candidates.length === 0) return null;

  return [...candidates].sort((a, b) => {
    const priorityDelta =
      ANNOUNCEMENT_TYPE_PRIORITY[a.type] - ANNOUNCEMENT_TYPE_PRIORITY[b.type];
    if (priorityDelta !== 0) return priorityDelta;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  })[0];
}

const DASHBOARD_NEWS_BANNER_TYPES = new Set<AnnouncementType>([
  AnnouncementType.News,
  AnnouncementType.NewVersion,
]);

/**
 * Targets that mirror a left-panel link gated on a tenant module: the
 * announcement shows only if the tenant has the corresponding module (or,
 * for AddressBook, any one of several modules) enabled, exactly like
 * MainNavigation gates the matching sidebar link.
 */
const ANNOUNCEMENT_TARGET_MODULE: Partial<Record<AnnouncementTarget, TenantModule | TenantModule[]>> = {
  [AnnouncementTarget.Stock]:          TenantModule.Warehouse,
  [AnnouncementTarget.InboundOrders]:  TenantModule.Warehouse,
  [AnnouncementTarget.OutboundOrders]: TenantModule.Warehouse,
  [AnnouncementTarget.RoadTransport]:  TenantModule.RoadTransport,
  [AnnouncementTarget.Billing]:        TenantModule.Billing,
  [AnnouncementTarget.Containers]:     TenantModule.Containers,
  [AnnouncementTarget.AddressBook]:    [TenantModule.DraftOutboundOrders, TenantModule.DraftRoadTransportOrders],
};

/**
 * Targets that mirror a left-panel link with no required module (always shown),
 * plus the dashboard sentinels.
 */
const ALWAYS_VISIBLE_TARGETS = new Set<AnnouncementTarget>([
  AnnouncementTarget.All,
  AnnouncementTarget.Dashboard,
]);

function appliesToDashboard(
  appliesTo: AnnouncementTarget[],
  tenantModules: TenantModule[],
): boolean {
  return appliesTo.some((target) => {
    if (ALWAYS_VISIBLE_TARGETS.has(target)) return true;
    const module = ANNOUNCEMENT_TARGET_MODULE[target];
    if (!module) return false;
    const required = Array.isArray(module) ? module : [module];
    return required.some((m) => tenantModules.includes(m));
  });
}

export function getDashboardNewsBannerAnnouncementIds(
  items: AnnouncementResponse[] | undefined,
  tenantModules: TenantModule[],
): string[] {
  return (items ?? [])
    .filter((item) => DASHBOARD_NEWS_BANNER_TYPES.has(item.type) && appliesToDashboard(item.appliesTo, tenantModules))
    .map((item) => item.id);
}

export function pickDashboardNewsBannerAnnouncement(
  items: AnnouncementResponse[] | undefined,
  tenantModules: TenantModule[],
  dismissedIds?: ReadonlySet<string>,
): AnnouncementResponse | null {
  const candidates = (items ?? []).filter(
    (item) =>
      DASHBOARD_NEWS_BANNER_TYPES.has(item.type) &&
      !dismissedIds?.has(item.id) &&
      appliesToDashboard(item.appliesTo, tenantModules),
  );
  if (candidates.length === 0) return null;

  return [...candidates].sort((a, b) => {
    const priorityDelta =
      ANNOUNCEMENT_TYPE_PRIORITY[a.type] - ANNOUNCEMENT_TYPE_PRIORITY[b.type];
    if (priorityDelta !== 0) return priorityDelta;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  })[0];
}
